import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
  Settings as SettingsIcon,
  Save,
  RefreshCw,
  Globe,
  Bell,
  Shield,
  Database,
  Palette,
  CheckCircle,
  AlertCircle,
  Moon,
  Sun,
  Smartphone,
  HardDrive,
  FileText,
  Zap,
  Download,
  RotateCcw,
  Clock,
  Play
} from 'lucide-react';
import './Settings.css';

function Settings() {
  const [settings, setSettings] = useState({
    siteName: 'Ballet Post',
    language: 'ru',
    timezone: 'Europe/Moscow',
    notifications: true,
    autoPost: false,
    postInterval: 60,
    maxNewsPerDay: 15
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [theme, setTheme] = useState('dark');
  const [miniappSize, setMiniappSize] = useState({ screenWidth: 0, screenHeight: 0 });
  const [backups, setBackups] = useState({});
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [prompts, setPrompts] = useState([]);
  const [promptEdits, setPromptEdits] = useState({});
  const [promptSaving, setPromptSaving] = useState({});
  const [promptMessage, setPromptMessage] = useState({});
  const [comicSettings, setComicSettings] = useState({ jpegQuality: 0 });
  const [comicSaving, setComicSaving] = useState(false);
  const [comicMessage, setComicMessage] = useState('');

  useEffect(() => {
    loadSettings();
    loadBackups();
    loadPrompts();
    loadComicSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const [settingsRes, miniappRes] = await Promise.all([
        api.get('/api/admin/settings'),
        api.get('/api/admin/website/settings')
      ]);
      if (settingsRes.data.success) {
        setSettings(prev => ({
          ...prev,
          ...settingsRes.data.settings
        }));
      }
      if (miniappRes.data.success) {
        setMiniappSize(miniappRes.data.settings);
      }
    } catch (error) {
      console.error('Ошибка загрузки настроек:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBackups = async () => {
    try {
      const res = await api.get('/api/admin/backup/list');
      if (res.data.success) {
        setBackups(res.data.backups);
      }
    } catch (error) {
      console.error('Ошибка загрузки бэкапов:', error);
    }
  };

  const loadPrompts = async () => {
    try {
      const res = await api.get('/api/admin/prompts');
      if (res.data.success) {
        setPrompts(res.data.prompts || []);
        const edits = {};
        (res.data.prompts || []).forEach(p => {
          edits[p.prompt_key] = p.prompt_text || '';
        });
        setPromptEdits(edits);
      }
    } catch (error) {
      console.error('Ошибка загрузки промптов:', error);
    }
  };

  const savePrompt = async (key) => {
    setPromptSaving(prev => ({ ...prev, [key]: true }));
    setPromptMessage(prev => ({ ...prev, [key]: '' }));
    try {
      const res = await api.put(`/api/admin/prompts/${key}`, {
        prompt_text: promptEdits[key]
      });
      if (res.data.success) {
        setPromptMessage(prev => ({ ...prev, [key]: 'saved' }));
        setTimeout(() => setPromptMessage(prev => ({ ...prev, [key]: '' })), 3000);
      } else {
        setPromptMessage(prev => ({ ...prev, [key]: 'error' }));
      }
    } catch (error) {
      setPromptMessage(prev => ({ ...prev, [key]: 'error' }));
    } finally {
      setPromptSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const loadComicSettings = async () => {
    try {
      const res = await api.get('/api/admin/comic-generator/settings');
      if (res.data.success && res.data.settings) {
        setComicSettings(prev => ({ ...prev, jpegQuality: res.data.settings.jpegQuality || 0 }));
      }
    } catch (error) {
      console.error('Ошибка загрузки настроек комиксов:', error);
    }
  };

  const saveComicSettings = async () => {
    setComicSaving(true);
    setComicMessage('');
    try {
      const res = await api.post('/api/admin/comic-generator/settings', {
        jpegQuality: comicSettings.jpegQuality
      });
      if (res.data.success) {
        setComicMessage('saved');
        setTimeout(() => setComicMessage(''), 3000);
      } else {
        setComicMessage('error');
      }
    } catch (error) {
      setComicMessage('error');
    } finally {
      setComicSaving(false);
    }
  };

  const handleBackupRun = async () => {
    setBackupLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await api.post('/api/admin/backup/run');
      if (res.data.success) {
        setMessage({ type: 'success', text: `Бэкап создан (${res.data.size})` });
        loadBackups();
      } else {
        setMessage({ type: 'error', text: res.data.error || 'Ошибка бэкапа' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Ошибка подключения к серверу' });
    } finally {
      setBackupLoading(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const handleRestore = async (backupPath) => {
    if (!window.confirm('Восстановить БД из этого бэкапа? Текущие данные будут заменены.')) return;
    setRestoring(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await api.post('/api/admin/backup/restore', { backupPath });
      if (res.data.success) {
        setMessage({ type: 'success', text: 'БД восстановлена. Сервисы перезапускаются...' });
      } else {
        setMessage({ type: 'error', text: res.data.error || 'Ошибка восстановления' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Ошибка подключения к серверу' });
    } finally {
      setRestoring(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const handleRestartServices = async () => {
    if (!window.confirm('Перезапустить все PM2 сервисы?')) return;
    setRestarting(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await api.post('/api/admin/services/restart');
      if (res.data.success) {
        setMessage({ type: 'success', text: 'Сервисы перезапущены' });
      } else {
        setMessage({ type: 'error', text: res.data.error || 'Ошибка перезапуска' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Ошибка подключения к серверу' });
    } finally {
      setRestarting(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  // Форматирование даты бэкапа
  const formatBackupDate = (isoDate) => {
    const d = new Date(isoDate);
    return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // Все бэкапы плоским списком, отсортированные по дате
  const allBackups = Object.entries(backups).flatMap(([cat, files]) =>
    (files || []).map(f => ({ ...f, category: cat }))
  ).sort((a, b) => new Date(b.date) - new Date(a.date));

  const categoryLabels = { daily: 'Ежедневный', weekly: 'Еженедельный', monthly: 'Ежемесячный', manual: 'Ручной' };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    
    try {
      const [settingsRes, miniappRes] = await Promise.all([
        api.post('/api/admin/settings', settings),
        api.post('/api/admin/website/settings', miniappSize)
      ]);
      if (settingsRes.data.success && miniappRes.data.success) {
        setMessage({ type: 'success', text: 'Настройки сохранены' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        setMessage({ type: 'error', text: settingsRes.data.message || miniappRes.data.error || 'Ошибка сохранения' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Ошибка подключения к серверу' });
    } finally {
      setSaving(false);
    }
  };

  const timezones = [
    { value: 'Europe/Moscow', label: 'Москва (UTC+3)' },
    { value: 'Europe/London', label: 'Лондон (UTC+0)' },
    { value: 'America/New_York', label: 'Нью-Йорк (UTC-5)' },
    { value: 'Asia/Tokyo', label: 'Токио (UTC+9)' }
  ];

  const languages = [
    { value: 'ru', label: 'Русский' },
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Español' },
    { value: 'de', label: 'Deutsch' }
  ];

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <span>Загрузка настроек...</span>
      </div>
    );
  }

  return (
    <div className="settings-page fade-in">
      <div className="page-header">
        <h1 className="page-title">Настройки</h1>
        <button 
          className="btn btn-primary" 
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <RefreshCw size={18} className="spin" />
              Сохранение...
            </>
          ) : (
            <>
              <Save size={18} />
              Сохранить
            </>
          )}
        </button>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'}`}>
          {message.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          {message.text}
        </div>
      )}

      <div className="settings-sections">
        {/* General Settings */}
        <div className="card settings-section">
          <div className="section-header">
            <div className="section-icon">
              <Globe size={20} />
            </div>
            <div className="section-info">
              <h3>Основные настройки</h3>
              <p>Базовые параметры системы</p>
            </div>
          </div>
          <div className="section-content">
            <div className="settings-row">
              <div className="input-group">
                <label className="input-label">Название сайта</label>
                <input
                  type="text"
                  className="input"
                  value={settings.siteName}
                  onChange={(e) => setSettings({...settings, siteName: e.target.value})}
                />
              </div>
              <div className="input-group">
                <label className="input-label">Язык по умолчанию</label>
                <select
                  className="input select"
                  value={settings.language}
                  onChange={(e) => setSettings({...settings, language: e.target.value})}
                >
                  {languages.map(l => (
                    <option key={l.value} value={l.value}>{l.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="input-group">
              <label className="input-label">Часовой пояс</label>
              <select
                className="input select"
                value={settings.timezone}
                onChange={(e) => setSettings({...settings, timezone: e.target.value})}
              >
                {timezones.map(tz => (
                  <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Auto-posting Settings */}
        <div className="card settings-section">
          <div className="section-header">
            <div className="section-icon">
              <Bell size={20} />
            </div>
            <div className="section-info">
              <h3>Автопостинг</h3>
              <p>Настройки автоматической публикации</p>
            </div>
          </div>
          <div className="section-content">
            <div className="toggle-setting">
              <div className="toggle-info">
                <span className="toggle-label">Автоматическая публикация</span>
                <span className="toggle-description">Автоматически публиковать новости в ботов</span>
              </div>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={settings.autoPost}
                  onChange={(e) => setSettings({...settings, autoPost: e.target.checked})}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
            
            <div className="settings-row">
              <div className="input-group">
                <label className="input-label">Интервал публикации (минуты)</label>
                <input
                  type="number"
                  className="input"
                  value={settings.postInterval}
                  onChange={(e) => setSettings({...settings, postInterval: parseInt(e.target.value)})}
                  min="5"
                  max="1440"
                />
              </div>
              <div className="input-group">
                <label className="input-label">Максимум новостей в день</label>
                <input
                  type="number"
                  className="input"
                  value={settings.maxNewsPerDay}
                  onChange={(e) => setSettings({...settings, maxNewsPerDay: parseInt(e.target.value)})}
                  min="1"
                  max="100"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Theme Settings */}
        <div className="card settings-section">
          <div className="section-header">
            <div className="section-icon">
              <Palette size={20} />
            </div>
            <div className="section-info">
              <h3>Внешний вид</h3>
              <p>Настройки темы и оформления</p>
            </div>
          </div>
          <div className="section-content">
            <div className="theme-selector">
              <button 
                className={`theme-option ${theme === 'dark' ? 'active' : ''}`}
                onClick={() => setTheme('dark')}
              >
                <Moon size={24} />
                <span>Тёмная тема</span>
              </button>
              <button 
                className={`theme-option ${theme === 'light' ? 'active' : ''}`}
                onClick={() => setTheme('light')}
              >
                <Sun size={24} />
                <span>Светлая тема</span>
              </button>
            </div>
          </div>
        </div>

        {/* Miniapp Size Settings */}
        <div className="card settings-section">
          <div className="section-header">
            <div className="section-icon">
              <Smartphone size={20} />
            </div>
            <div className="section-info">
              <h3>📱 Мини-приложение</h3>
              <p>Размер окна для ПК (на телефонах всегда 100%)</p>
            </div>
          </div>
          <div className="section-content">
            <div className="settings-row">
              <div className="input-group">
                <label className="input-label">Ширина (px)</label>
                <input
                  type="number"
                  className="input"
                  value={miniappSize.screenWidth || ''}
                  onChange={(e) => setMiniappSize({...miniappSize, screenWidth: parseInt(e.target.value) || 0})}
                  min="0"
                  max="3000"
                  placeholder="0 = авто"
                />
              </div>
              <div className="input-group">
                <label className="input-label">Высота (px)</label>
                <input
                  type="number"
                  className="input"
                  value={miniappSize.screenHeight || ''}
                  onChange={(e) => setMiniappSize({...miniappSize, screenHeight: parseInt(e.target.value) || 0})}
                  min="0"
                  max="3000"
                  placeholder="0 = авто"
                />
              </div>
            </div>
            <p style={{fontSize: '12px', color: 'var(--text-muted)', marginTop: '8px'}}>
              Укажите 0 для автоматического размера. Настройка влияет только на ПК версию Telegram.
            </p>
          </div>
        </div>

        {/* Database Info */}
        <div className="card settings-section">
          <div className="section-header">
            <div className="section-icon">
              <Database size={20} />
            </div>
            <div className="section-info">
              <h3>База данных</h3>
              <p>Информация о системе</p>
            </div>
          </div>
          <div className="section-content">
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Версия</span>
                <span className="info-value">3.0.0</span>
              </div>
              <div className="info-item">
                <span className="info-label">База данных</span>
                <span className="info-value">SQLite</span>
              </div>
              <div className="info-item">
                <span className="info-label">Node.js</span>
                <span className="info-value">18.x</span>
              </div>
              <div className="info-item">
                <span className="info-label">Порт админки</span>
                <span className="info-value">4000</span>
              </div>
            </div>
          </div>
        </div>

        {/* Сжатие комиксов */}
        <div className="card settings-section">
          <div className="section-header">
            <div className="section-icon">
              <Zap size={20} />
            </div>
            <div className="section-info">
              <h3>Сжатие изображений</h3>
              <p>JPEG/WebP-сжатие комиксов и всех новостных картинок в миниаппе</p>
            </div>
          </div>
          <div className="section-content">
            <div className="setting-row-inline">
              <label className="setting-label">JPEG Quality</label>
              <div className="setting-control">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={comicSettings.jpegQuality}
                  onChange={(e) => setComicSettings(prev => ({ ...prev, jpegQuality: parseInt(e.target.value) }))}
                  className="quality-slider"
                />
                <span className="quality-value">
                  {comicSettings.jpegQuality === 0 ? 'Выкл (PNG)' : `${comicSettings.jpegQuality}%`}
                </span>
              </div>
              <p className="setting-hint">
                0 = без сжатия (оригинал). 70-85 = оптимальное соотношение качества и размера. Влияет на комиксы и все картинки в новостях миниаппа.
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '1rem' }}>
              <button
                className="btn btn-primary btn-sm"
                onClick={saveComicSettings}
                disabled={comicSaving}
              >
                {comicSaving ? (
                  <><RefreshCw size={14} className="spin" /> Сохранение...</>
                ) : (
                  <><Save size={14} /> Сохранить</>
                )}
              </button>
              {comicMessage === 'saved' && (
                <span style={{ color: 'var(--success)', fontSize: '13px' }}>
                  <CheckCircle size={14} style={{ verticalAlign: 'middle' }} /> Сохранено
                </span>
              )}
              {comicMessage === 'error' && (
                <span style={{ color: 'var(--danger)', fontSize: '13px' }}>
                  <AlertCircle size={14} style={{ verticalAlign: 'middle' }} /> Ошибка
                </span>
              )}
            </div>
          </div>
        </div>

        {/* AI Промпты */}
        <div className="card settings-section">
          <div className="section-header">
            <div className="section-icon">
              <FileText size={20} />
            </div>
            <div className="section-info">
              <h3>AI Промпты</h3>
              <p>Редактирование системных промптов для обработки новостей и генерации комиксов</p>
            </div>
          </div>
          <div className="section-content">
            {prompts.length === 0 ? (
              <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
                Промпты не найдены. Они появятся после первого запуска обработки новостей.
              </p>
            ) : (
              prompts.map(p => (
                <div key={p.prompt_key} className="prompt-editor">
                  <div className="prompt-editor-header">
                    <div>
                      <strong>{p.prompt_name || p.prompt_key}</strong>
                      {p.description && (
                        <span style={{ color: 'var(--text-muted)', fontSize: '12px', marginLeft: '8px' }}>
                          {p.description}
                        </span>
                      )}
                    </div>
                    <div className="prompt-editor-actions">
                      {promptMessage[p.prompt_key] === 'saved' && (
                        <span style={{ color: 'var(--success)', fontSize: '13px', marginRight: '8px' }}>
                          <CheckCircle size={14} style={{ verticalAlign: 'middle' }} /> Сохранено
                        </span>
                      )}
                      {promptMessage[p.prompt_key] === 'error' && (
                        <span style={{ color: 'var(--danger)', fontSize: '13px', marginRight: '8px' }}>
                          <AlertCircle size={14} style={{ verticalAlign: 'middle' }} /> Ошибка
                        </span>
                      )}
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => savePrompt(p.prompt_key)}
                        disabled={promptSaving[p.prompt_key]}
                      >
                        {promptSaving[p.prompt_key] ? (
                          <><RefreshCw size={14} className="spin" /> Сохранение...</>
                        ) : (
                          <><Save size={14} /> Сохранить</>
                        )}
                      </button>
                    </div>
                  </div>
                  <textarea
                    className="prompt-editor-textarea"
                    value={promptEdits[p.prompt_key] || ''}
                    onChange={(e) => setPromptEdits(prev => ({
                      ...prev,
                      [p.prompt_key]: e.target.value
                    }))}
                    rows={8}
                  />
                  <div className="prompt-editor-meta">
                    <span>{(promptEdits[p.prompt_key] || '').length} символов</span>
                    {p.prompt_category && <span>Категория: {p.prompt_category}</span>}
                    {p.updated_at && <span>Обновлён: {new Date(p.updated_at).toLocaleString('ru-RU')}</span>}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Backup & System */}
        <div className="card settings-section">
          <div className="section-header">
            <div className="section-icon">
              <HardDrive size={20} />
            </div>
            <div className="section-info">
              <h3>Бэкапы и система</h3>
              <p>Резервное копирование БД и управление сервисами</p>
            </div>
          </div>
          <div className="section-content">
            {/* Кнопки действий */}
            <div className="backup-actions">
              <button
                className="btn btn-primary"
                onClick={handleBackupRun}
                disabled={backupLoading}
              >
                {backupLoading ? (
                  <><RefreshCw size={16} className="spin" /> Создание...</>
                ) : (
                  <><Download size={16} /> Создать бэкап</>
                )}
              </button>
              <button
                className="btn btn-secondary"
                onClick={handleRestartServices}
                disabled={restarting}
              >
                {restarting ? (
                  <><RefreshCw size={16} className="spin" /> Перезапуск...</>
                ) : (
                  <><Play size={16} /> Перезапустить сервисы</>
                )}
              </button>
            </div>

            {/* Cron информация */}
            <div className="backup-cron-info">
              <Clock size={14} />
              <span>Автобэкап: ежедневно в 03:00 (cron). Хранение: 7 дней / 52 нед. / 12 мес.</span>
            </div>

            {/* Список бэкапов */}
            {allBackups.length > 0 ? (
              <div className="backup-list">
                <div className="backup-list-header">
                  <span>Доступные бэкапы ({allBackups.length})</span>
                  <button className="btn-link" onClick={loadBackups}>
                    <RefreshCw size={14} /> Обновить
                  </button>
                </div>
                <div className="backup-items">
                  {allBackups.slice(0, 15).map((b, i) => (
                    <div key={i} className="backup-item">
                      <div className="backup-item-info">
                        <span className="backup-item-name">{b.name}</span>
                        <span className="backup-item-meta">
                          <span className={`backup-badge backup-badge-${b.category}`}>
                            {categoryLabels[b.category] || b.category}
                          </span>
                          {b.sizeHuman} &middot; {formatBackupDate(b.date)}
                        </span>
                      </div>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleRestore(b.path)}
                        disabled={restoring}
                        title="Восстановить из этого бэкапа"
                      >
                        <RotateCcw size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p style={{ color: 'var(--text-muted)', fontSize: '13px', marginTop: '16px' }}>
                Бэкапы пока не созданы. Нажмите "Создать бэкап" или дождитесь автоматического.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Settings;
